/** `state` key carrying the origin + path the sign-in started from. */
export const RETURN_TARGET_STATE_KEY = '__returnTo'

const TESTING_HOST = 'monorepo-rp-customer-profile-testing.vercel.app'

// Deployment hosts of the testing Vercel project: `-<hash>-` per deployment and
// `-git-<branch>-` per branch. The acceptance and production projects have
// different name prefixes, so they never match.
const TESTING_PREVIEW_HOST =
  /^monorepo-rp-customer-profile-testing-(?:git-[a-z0-9-]+|[a-z0-9]{9})-suitsupply-team\.vercel\.app$/

function isTestingHost(url: URL): boolean {
  return (
    url.protocol === 'https:' &&
    url.port === '' &&
    (url.hostname === TESTING_HOST || TESTING_PREVIEW_HOST.test(url.hostname))
  )
}

/** Where to send the user after sign-in: the page they opened, origin included. */
export function buildReturnTarget(
  location: Pick<Location, 'origin' | 'pathname'>,
): string {
  return `${location.origin}${location.pathname}`
}

/**
 * Entra only returns to the registered redirect URI, which is the testing
 * host, so a preview's sign-in lands there. The testing host relays the user
 * on to the preview that started it — but only to testing hosts, and only from
 * a testing build. `state` is attacker-controlled, so anything else falls back
 * to the current origin instead of being followed.
 */
export function resolveReturnTarget(
  returnTo: unknown,
  currentOrigin: string,
  env: string,
): URL {
  const fallback = new URL('/', currentOrigin)

  if (typeof returnTo !== 'string') {
    return fallback
  }

  let target: URL
  try {
    target = new URL(returnTo)
  } catch {
    return fallback
  }

  if (target.origin === currentOrigin) {
    return new URL(target.pathname, currentOrigin)
  }

  if (env === 'tst' && isTestingHost(target)) {
    return new URL(target.pathname, target.origin)
  }

  return fallback
}
